import { RootState } from "@/stores/rootReducer";
import client from "@/utils/api/client";
import {
  ListItemResponse,
  ListResponse,
  ListUpdateParam,
  UploadImageResponse,
} from "@/web-client/api";
import { Align } from "@/consts/Align";
import StateContext, { IStateContext } from "@/contexts/StateContext";
import useProgress from "@/hooks/useProgress";
import { useRouter } from "next/router";
import {
  Dispatch,
  FC,
  SetStateAction,
  useCallback,
  useContext,
  useMemo,
} from "react";
import { useForm } from "react-hook-form";
import { useSelector } from "react-redux";
import ReactTextareaAutosize from "react-textarea-autosize";
import FormUnit from "@/components/FormUnit";
import ImageInputView from "@/components/ImageInputView";
import InputLayout from "@/components/InputLayout";
import InputToggleField from "@/components/InputToggleField";
import { ModalEditListPhase } from "@/features/list/components/ModalEditList";
import Button from "@/components/Button";

type Props = {
  list: ListResponse;
  move: Dispatch<SetStateAction<ModalEditListPhase>>;
  listItemForCover?: ListItemResponse;
  onComplete?: VoidFunction;
};

const EditListTemplate: FC<Props> = ({
  list,
  move,
  listItemForCover,
  onComplete,
}): JSX.Element => {
  const router = useRouter();
  const progress = useProgress();
  const { update } = useContext<IStateContext<ListResponse>>(StateContext);
  const { user } = useSelector((state: RootState) => state.auth);
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ListUpdateParam>({
    defaultValues: {
      ...list,
    },
  });

  const onSubmit = useCallback(
    (data: ListUpdateParam) => {
      progress(
        async () => {
          const { data: updatedList } = await client.listsIdPut({
            id: list.id,
            listUpdateParam: {
              name: data.name,
              description: data.description,
              is_private: data.is_private,
              main_item_id: listItemForCover
                ? listItemForCover.id
                : list.main_item_id,
            },
          });
          update(updatedList);

          onComplete && onComplete();
        },
        {
          successMessage: "リストを更新しました",
          errorMessage: "リストの更新に失敗しました",
        },
      );
    },
    [list, listItemForCover, onComplete],
  );

  const deleteHandler = () => {
    progress(
      async () => {
        if (
          window.confirm(
            "リストを削除します。この操作は取り消せません。削除してもよろしいですか？",
          )
        ) {
          await client.listsIdDelete({ id: list.id });
          router.push(`/users/${user.id}/lists`);
        }
      },
      { errorMessage: "リストの削除に失敗しました" },
    );
  };

  const defaultImage = useMemo<UploadImageResponse | null>(() => {
    if (listItemForCover) return listItemForCover.upload_image;

    if (list.item_images.length > 0) return list.item_images[0];

    return null;
  }, [listItemForCover, list]);

  return (
    <InputLayout title="リストを編集する" align={Align.Left}>
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-16">
        <FormUnit id="main_item_id" label="カバー画像">
          <button onClick={() => move(ModalEditListPhase.SelectCover)}>
            <ImageInputView
              imageSrc={defaultImage ? defaultImage.urls.small : ""}
            />
          </button>
        </FormUnit>
        <FormUnit
          id="name"
          label="名前"
          error={errors.name?.message}
          attention="required"
        >
          <input
            className="w-full"
            type="text"
            placeholder="お気に入りリスト"
            {...register("name", { required: "名前は必須です" })}
          />
        </FormUnit>
        <FormUnit id="description" label="リストの説明">
          <ReactTextareaAutosize
            minRows={3}
            className="w-full"
            {...register("description")}
          />
        </FormUnit>
        <FormUnit id="private" label="リストを非公開にする">
          <InputToggleField
            id="is_private"
            name="is_private"
            register={register}
            defaultChecked={list.is_private}
          ></InputToggleField>
        </FormUnit>
        <div className="sticky bottom-0 flex justify-between py-8 bg-white bg-opacity-80">
          <Button onClick={deleteHandler} type="button" color="danger">
            削除
          </Button>

          <Button type="submit">更新</Button>
        </div>
      </form>
    </InputLayout>
  );
};

export default EditListTemplate;
